import { useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";

import {
  useActiveCustomFieldsAndGroups,
  useCustomFieldGroupsByIds,
  useCustomFieldMutation,
} from "@web-app/api";
import {
  CustomField,
  CustomFieldValue,
  Order,
  OrderCustomField,
} from "@web-app/types";

import { useAlert } from "../Alert/useAlert";
import ErrorMessage from "../ErrorMessage";
import Tabs from "../Tabs";

import { DropdownField } from "./DropdownField";
import { NumberField } from "./NumberField";
import { TextField } from "./TextField";
import { mapCustomFieldsGroupData } from "./utils";

interface CustomFieldsProps {
  customFieldsState?: Array<{
    definition: CustomField;
    values: CustomFieldValue[];
  }>;
  entity: "order" | "workspace";
  entityId?: string;
  entityObject?: Order;
  isReadOnly?: boolean;
  onChange?: (
    fieldId: string,
    customField: CustomField,
    value: CustomField["defaultValues"],
  ) => void;
  workspaceId?: string;
}

export default function CustomFields(props: CustomFieldsProps) {
  const {
    entity,
    entityId,
    workspaceId,
    isReadOnly,
    onChange,
    entityObject,
    customFieldsState,
  } = props;
  const { updateOrderCustomField } = useCustomFieldMutation();
  const { showAlertAsync } = useAlert();
  const onChangeHandler = async (
    fieldId: string,
    customField: CustomField,
    value: CustomField["defaultValues"],
  ) => {
    if (onChange) {
      onChange(fieldId, customField, value);
      return;
    }

    if (entity === "order" && entityId) {
      await showAlertAsync(
        updateOrderCustomField.mutateAsync({
          entityId: entityId,
          fieldId: fieldId,
          payload: { customFieldValues: value },
        }),
        {
          error: {
            message: "Failed to update custom field",
            title: "Error",
          },
          loading: {
            message: "Updating custom field...",
            title: "Updating...",
          },
          success: {
            message: `${customField.name} updated successfully`,
            title: "Success!",
          },
        },
      );
    }
  };

  if (entity === "order" && !entityId && workspaceId) {
    return (
      <ActiveCustomFields
        workspaceId={workspaceId}
        isReadOnly={isReadOnly}
        onChangeHandler={onChangeHandler}
        customFieldsState={customFieldsState}
      />
    );
  }

  if (entity === "order" && entityId && workspaceId && entityObject) {
    return (
      <OrderCustomFields
        workspaceId={workspaceId}
        isReadOnly={isReadOnly}
        onChangeHandler={onChangeHandler}
        order={entityObject}
      />
    );
  }

  return null;
}

interface ActiveCustomFieldsProps {
  customFieldsState?: Array<{
    definition: CustomField;
    values: CustomFieldValue[];
  }>;
  isReadOnly?: boolean;
  onChangeHandler: (
    fieldId: string,
    customField: CustomField,
    value: CustomField["defaultValues"],
    groupId: string,
  ) => void;
  workspaceId: string;
}

const ActiveCustomFields = ({
  workspaceId,
  isReadOnly,
  onChangeHandler,
  customFieldsState,
}: ActiveCustomFieldsProps) => {
  const [
    { data: customFieldsData, isSuccess: isSuccessCF },
    { data: customFieldsGroups, isSuccess: isSuccessCFG },
  ] = useActiveCustomFieldsAndGroups(workspaceId);

  const customFieldsWithGroup = useMemo(() => {
    if (
      !isSuccessCF ||
      !isSuccessCFG ||
      !customFieldsData?.length ||
      !customFieldsGroups?.length
    ) {
      return new Map();
    }

    const cfs: OrderCustomField[] = [];
    customFieldsData.forEach((field) => {
      cfs.push({
        createdAt: "",
        customField: field,
        customFieldId: field.id,
        customFieldValues: field.defaultValues ?? [],
        groupId: field.groupId,
        id: "field-id-" + field.name,
        orderId: "",
        updatedAt: "",
        workspaceId: workspaceId,
      });
    });

    const sortedCfs = cfs.sort((a, b) => {
      return a.customField.name.localeCompare(b.customField.name);
    });
    return mapCustomFieldsGroupData(customFieldsGroups, sortedCfs);
  }, [
    customFieldsData,
    customFieldsGroups,
    isSuccessCF,
    isSuccessCFG,
    workspaceId,
  ]);

  return (
    <CustomFieldsGroupAccordion
      fieldsGroups={customFieldsWithGroup}
      isReadOnly={isReadOnly}
      onChangeHandler={onChangeHandler}
      customFieldsState={customFieldsState}
    />
  );
};

interface OrderCustomFieldsProps {
  isReadOnly?: boolean;
  onChangeHandler: (
    fieldId: string,
    customField: CustomField,
    value: CustomField["defaultValues"],
    groupId: string,
  ) => void;
  order: Order;
  workspaceId: string;
}

const OrderCustomFields = ({
  workspaceId,
  isReadOnly,
  onChangeHandler,
  order,
}: OrderCustomFieldsProps) => {
  const groupIds = useMemo(
    () => [
      ...new Set(
        (order.customFields ?? [])
          .map((f) => f.definition.groupId)
          .filter(Boolean),
      ),
    ],
    [order.customFields],
  );

  const { data: customFieldsGroups } = useCustomFieldGroupsByIds(groupIds);

  const customFieldsWithGroup = useMemo(() => {
    if (!customFieldsGroups)
      return new Map<string, { fields: OrderCustomField[]; name: string }>();

    const cfs: OrderCustomField[] = (order.customFields ?? []).map((field) => ({
      createdAt: "",
      customField: field.definition,
      customFieldId: field.definition.id,
      customFieldValues: field.values ?? [],
      groupId: field.definition.groupId,
      id: "field-id-" + field.definition.name,
      orderId: "",
      updatedAt: "",
      workspaceId: workspaceId,
    }));

    const sortedCfs = cfs.sort((a, b) =>
      a.customField.name.localeCompare(b.customField.name),
    );

    return mapCustomFieldsGroupData(customFieldsGroups, sortedCfs);
  }, [customFieldsGroups, order.customFields, workspaceId]);

  if (customFieldsWithGroup.size) {
    return (
      <CustomFieldsGroupAccordion
        fieldsGroups={customFieldsWithGroup}
        isReadOnly={isReadOnly}
        onChangeHandler={onChangeHandler}
      />
    );
  }
  return null;
};

interface CustomFieldsGroupAccordionProps {
  customFieldsState?: Array<{
    definition: CustomField;
    values: CustomFieldValue[];
  }>;
  fieldsGroups: Map<
    string,
    {
      fields: OrderCustomField[];
      name: string;
    }
  >;
  isReadOnly?: boolean;
  onChangeHandler: (
    fieldId: string,
    customField: CustomField,
    value: CustomField["defaultValues"],
    groupId: string,
  ) => void;
}

const CustomFieldsGroupAccordion = ({
  fieldsGroups,
  isReadOnly = false,
  onChangeHandler,
  customFieldsState,
}: CustomFieldsGroupAccordionProps) => {
  const method = useFormContext();

  if (fieldsGroups && fieldsGroups.size === 0) {
    return <p className="text-sm text-gray-500">No custom fields available</p>;
  }

  const getCustomFieldStateValue = (customField: CustomField) => {
    return customFieldsState?.find(
      ({ definition }) => definition.id === customField.id,
    )?.values;
  };

  const groups = Array.from(fieldsGroups);
  const tabs = groups.map(([groupId, { fields, name }]) => ({
    content: (
      <div className="space-y-3 lg:space-y-4 mt-2">
        {fields.length === 0 ? (
          <p className="text-xs lg:text-sm text-gray-500">
            No custom fields available
          </p>
        ) : (
          <div className="space-y-3 lg:space-y-4">
            {fields.map((customField) => {
              const fieldValuesFromState = getCustomFieldStateValue(
                customField.customField,
              );
              const fieldValues = fieldValuesFromState?.length
                ? fieldValuesFromState
                : customField.customFieldValues;

              switch (customField.customField.type) {
                case "TEXT":
                  return (
                    <div key={customField.id} className="relative">
                      {method ? (
                        <Controller
                          name={customField.customField.name}
                          control={method.control}
                          render={({
                            field: { onChange, ...field },
                            fieldState: { error },
                          }) => (
                            <>
                              <TextField
                                fieldId={customField.id}
                                customField={customField.customField}
                                fieldValues={fieldValues}
                                isReadOnly={isReadOnly}
                                onChange={(
                                  fieldId: string,
                                  customField: CustomField,
                                  value: CustomField["defaultValues"],
                                  groupId: string,
                                ) => {
                                  onChange(value[0].value || undefined);
                                  onChangeHandler(
                                    fieldId,
                                    customField,
                                    value,
                                    groupId,
                                  );
                                }}
                                isRequired={customField.customField.isRequired}
                                {...field}
                              />
                              <ErrorMessage message={error?.message} />
                            </>
                          )}
                        />
                      ) : (
                        <TextField
                          fieldId={customField.id}
                          customField={customField.customField}
                          fieldValues={fieldValues}
                          isReadOnly={isReadOnly}
                          onChange={onChangeHandler}
                          isRequired={customField.customField.isRequired}
                          isDebounce
                        />
                      )}
                    </div>
                  );
                case "NUMBER":
                  return (
                    <div key={customField.id} className="relative">
                      {method ? (
                        <Controller
                          name={customField.customField.name}
                          control={method.control}
                          render={({
                            field: { onChange, ...field },
                            fieldState: { error },
                          }) => (
                            <>
                              <NumberField
                                fieldId={customField.id}
                                customField={customField.customField}
                                fieldValues={fieldValues}
                                isReadOnly={isReadOnly}
                                onChange={(
                                  fieldId: string,
                                  customField: CustomField,
                                  value: CustomField["defaultValues"],
                                  groupId: string,
                                ) => {
                                  onChange(value[0].value || undefined);
                                  onChangeHandler(
                                    fieldId,
                                    customField,
                                    value,
                                    groupId,
                                  );
                                }}
                                isRequired={customField.customField.isRequired}
                                {...field}
                              />
                              <ErrorMessage message={error?.message} />
                            </>
                          )}
                        />
                      ) : (
                        <NumberField
                          fieldId={customField.id}
                          customField={customField.customField}
                          fieldValues={fieldValues}
                          isReadOnly={isReadOnly}
                          onChange={onChangeHandler}
                          isRequired={customField.customField.isRequired}
                          isDebounce
                        />
                      )}
                    </div>
                  );
                case "DROPDOWN":
                  return (
                    <div key={customField.id} className="relative">
                      {method ? (
                        <Controller
                          name={customField.customField.name}
                          control={method.control}
                          render={({
                            field: { onChange, ...field },
                            fieldState: { error },
                          }) => (
                            <>
                              <DropdownField
                                fieldId={customField.id}
                                customField={customField.customField}
                                fieldValues={fieldValues}
                                isReadOnly={isReadOnly}
                                onChange={(
                                  fieldId: string,
                                  customField: CustomField,
                                  value: CustomField["defaultValues"],
                                  groupId: string,
                                ) => {
                                  onChange(value);
                                  onChangeHandler(
                                    fieldId,
                                    customField,
                                    value,
                                    groupId,
                                  );
                                }}
                                isRequired={customField.customField.isRequired}
                                {...field}
                              />
                              <ErrorMessage message={error?.message} />
                            </>
                          )}
                        />
                      ) : (
                        <DropdownField
                          fieldId={customField.id}
                          customField={customField.customField}
                          fieldValues={fieldValues}
                          isReadOnly={isReadOnly}
                          onChange={onChangeHandler}
                          isRequired={customField.customField.isRequired}
                          isDebounce
                        />
                      )}
                    </div>
                  );
                default:
                  return null;
              }
            })}
          </div>
        )}
      </div>
    ),
    id: `${name} (${fields.length})`,
    key: groupId,
    title: (
      <div className="tw-sidebar-tabs-style flex">
        {name} ({fields.length})
      </div>
    ),
  }));

  return <Tabs tabs={tabs} />;
};
